import styles from "./index.module.scss";
import CalcFormItem from "./CalcFormItem";
import CalcFormInput from "./CalcFormInput";
import CalcFormLayoutGroup from "./CalcFormLayoutGroup";
import Button from "../../UI/Button";
import React, { useEffect, useState } from "react";
import CalcFormRadioButton from "./CalcFormRadioButton";
import { useCalculatorController } from "../context/context";
import { useFieldArray, useForm } from "react-hook-form";
import scrollToElement from "../../../utils/scrollToElement";
import {
  setCalcFormData,
  setRenewalFieldCount,
  setShowGratitudeModal,
  submitCalcForm,
  submitMetricForm,
  triggerCalcFormValidation,
} from "../context/actions";
import useWindowWidth from "../../../hooks/useWindowWidth";
import cn from "classnames";
import { RENEWAL_FIELD_MAX_COUNT } from "../constants";

const CalcForm = () => {
  const { controller, dispatch } = useCalculatorController();
  const {
    isCalcFormValidationTriggered,
    isMetricFormSubmitted,
    isGratitudeModalSubmitted,
  } = controller;
  const [renewalCount, setRenewalCount] = useState(1);
  const width = useWindowWidth();
  const [isTrial, setIsTrial] = useState(true);

  const {
    control,
    register,
    handleSubmit,
    trigger,
    setValue,
    formState: { errors },
  } = useForm({
    reValidateMode: "onSubmit",
    defaultValues: {
      installs: "",
      cost_per_install: "",
      average_subscription_price: "",
      platform_commission: "",
      cr_install_to_trial: "",
      cr_trial_to_subscription: "",
      cr_install_to_subscription: "",
      cr_subscription_to_renewals: [{ cr_subscription_to_renewal: "" }],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "cr_subscription_to_renewals",
  });

  useEffect(() => {
    if (isCalcFormValidationTriggered) {
      trigger().then((isValid) => {
        if (!isValid) {
          scrollToElement("revenue-calculator");
          triggerCalcFormValidation(dispatch, false);
        } else {
          handleSubmit(onSubmit)();
          submitMetricForm(dispatch, true);
        }
      });
    }
  }, [isCalcFormValidationTriggered]);

  const handleAddField = (fieldName) => {
    setRenewalCount((prevState) => prevState + 1);
    append({ [fieldName]: "" });
  };

  const handleRemoveField = (index) => {
    remove(index);
    setRenewalCount((prev) => prev - 1);
  };

  const onSubmit = (data) => {
    submitCalcForm(dispatch, true);
    setRenewalFieldCount(dispatch, renewalCount);
    setCalcFormData(dispatch, { ...data, isTrial });

    if (
      renewalCount === RENEWAL_FIELD_MAX_COUNT &&
      !isGratitudeModalSubmitted &&
      isMetricFormSubmitted
    ) {
      setShowGratitudeModal(dispatch, true);
    }
  };

  return (
    <div className={styles.wrapper}>
      <h2 className={cn("subheader-medium", styles.title)}>
        Put your data into the table
      </h2>

      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <CalcFormLayoutGroup mode={width > 640 ? "horizontal" : "vertical"}>
            <CalcFormItem label="Installs" htmlFor="installs" required>
              <CalcFormInput
                type="number"
                step="any"
                name="installs"
                id="installs"
                placeholder="1 000"
                {...register("installs", {
                  required: true,
                  min: 0,
                })}
                error={!!errors.installs}
              />
            </CalcFormItem>
            <CalcFormItem
              label="Cost per install ($)"
              htmlFor="cost_per_install"
            >
              <CalcFormInput
                type="number"
                step="any"
                name="cost_per_install"
                id="cost_per_install"
                placeholder="$1"
                {...register("cost_per_install", {
                  min: 0,
                })}
                error={!!errors.cost_per_install}
              />
            </CalcFormItem>
          </CalcFormLayoutGroup>

          <CalcFormLayoutGroup
            mode={width > 640 ? "horizontal" : "vertical"}
            offset="md"
          >
            <CalcFormItem
              required
              label="Average Subscription price($)"
              htmlFor="average_subscription_price"
              labelSize="small"
            >
              <CalcFormInput
                type="number"
                step="any"
                name="average_subscription_price"
                id="average_subscription_price"
                placeholder="$9.99"
                {...register("average_subscription_price", {
                  required: true,
                  min: 0,
                })}
                error={!!errors.average_subscription_price}
              />
            </CalcFormItem>
            <CalcFormItem
              label="Platform commission (%)"
              htmlFor="platform_commission"
            >
              <CalcFormInput
                type="number"
                step="any"
                name="platform_commission"
                id="platform_commission"
                placeholder="15%"
                {...register("platform_commission", { min: 0 })}
                error={!!errors.platform_commission}
              />
            </CalcFormItem>
          </CalcFormLayoutGroup>
        </div>

        <div>
          <CalcFormLayoutGroup mode="horizontal" offsetBottom="md">
            <CalcFormItem label="Trial">
              <div className={styles.radioGroupWrapper}>
                <CalcFormRadioButton
                  id="yes"
                  label="Yes"
                  defaultChecked
                  value={isTrial}
                  onChange={() => {
                    setIsTrial(true);
                    setValue("cr_install_to_subscription", "");
                  }}
                />
                <CalcFormRadioButton
                  id="no"
                  label="No"
                  value={!isTrial}
                  onChange={() => {
                    setIsTrial(false);
                    setValue("cr_install_to_trial", "");
                    setValue("cr_trial_to_subscription", "");
                  }}
                />
              </div>
            </CalcFormItem>
          </CalcFormLayoutGroup>

          <CalcFormLayoutGroup mode={width > 640 ? "horizontal" : "vertical"}>
            {isTrial ? (
              <>
                <CalcFormItem
                  required
                  label="CR Install to Trial (%)"
                  htmlFor="cr_install_to_trial"
                >
                  <CalcFormInput
                    type="number"
                    step="any"
                    name="cr_install_to_trial"
                    id="cr_install_to_trial"
                    placeholder="10%"
                    {...register("cr_install_to_trial", {
                      required: isTrial,
                      min: 0,
                    })}
                    error={!!errors.cr_install_to_trial}
                  />
                </CalcFormItem>
                <CalcFormItem
                  required
                  label="CR Trial to Subscription (%)"
                  htmlFor="cr_trial_to_subscription"
                >
                  <CalcFormInput
                    type="number"
                    step="any"
                    name="cr_trial_to_subscription"
                    id="cr_trial_to_subscription"
                    placeholder="10%"
                    {...register("cr_trial_to_subscription", {
                      required: isTrial,
                      min: 0,
                    })}
                    error={!!errors.cr_trial_to_subscription}
                  />
                </CalcFormItem>
              </>
            ) : (
              <CalcFormItem
                required
                label="CR Install to Subscription (%)"
                htmlFor="cr_install_to_subscription"
              >
                <CalcFormInput
                  type="number"
                  step="any"
                  name="cr_install_to_subscription"
                  id="cr_install_to_subscription"
                  placeholder="10%"
                  {...register("cr_install_to_subscription", {
                    required: !isTrial,
                    min: 0,
                  })}
                  error={!!errors.cr_install_to_subscription}
                />
              </CalcFormItem>
            )}
          </CalcFormLayoutGroup>

          <CalcFormLayoutGroup offset="md">
            {fields.map((field, idx) => (
              <CalcFormItem
                key={field.id}
                required
                label={`CR Subscription to Renewal ${idx + 1} (%)`}
                htmlFor={`cr_subscription_to_renewal_${idx + 1}`}
              >
                <CalcFormInput
                  type="number"
                  step="any"
                  name={`cr_subscription_to_renewal_${idx + 1}`}
                  id={`cr_subscription_to_renewal_${idx + 1}`}
                  onRemoveInputClick={() => handleRemoveField(idx)}
                  showTrashButton={idx !== 0}
                  placeholder="10%"
                  {...register(
                    `cr_subscription_to_renewals.${idx}.cr_subscription_to_renewal`,
                    {
                      required: true,
                      min: 0,
                    }
                  )}
                  error={
                    !!errors?.cr_subscription_to_renewals?.at(idx)
                      ?.cr_subscription_to_renewal
                  }
                />
              </CalcFormItem>
            ))}
          </CalcFormLayoutGroup>
        </div>

        <div className={styles.buttons}>
          <Button title="Get the result" fullWidth type="submit" />
          <Button
            disabled={renewalCount >= 10}
            transparent
            fullWidth
            title="Add the option"
            onClick={() => handleAddField("cr_subscription_to_renewal")}
          />
        </div>
      </form>
    </div>
  );
};

export default CalcForm;
